import { Show } from "@rtkwlf/c3-lib-components";
import { createContext, useContext } from "react";
import { jsx } from "react/jsx-runtime";
import { Store, useStore } from "@tanstack/react-store";

//#region src/providers/capabilities-provider.tsx
const CapabilitiesContext = createContext(void 0);
/**
* Provides component access to capabilities context
* @returns capabilities context
*/
const useCapabilitiesContext = () => {
	const context = useContext(CapabilitiesContext);
	if (!context) throw new Error("Component must be wrapped in CapabilitiesProvider in order to access CapabilitiesContext");
	return context;
};
const CapabilitiesProvider = ({ store, children }) => {
	return /* @__PURE__ */ jsx(CapabilitiesContext.Provider, {
		value: store,
		children
	});
};

//#endregion
//#region src/utils/has-capability.ts
/**
* Check if a user has a specific capability
*
* @param state state containing capabilities
* @param capability capability to check
* @returns true if the capability exists, false otherwise
*/
const hasCapability = (state, capability) => {
	return state.capabilities.has(capability);
};

//#endregion
//#region src/hooks/use-has-capability.ts
/**
* Check if a user has a specific capability
*
* @param capability capability to check
* @returns True if the capability exists, false otherwise
*/
const useHasCapability = (capability) => {
	/**
	* Get an instance of the capabilities context
	*/
	const { store } = useCapabilitiesContext();
	/**
	* Check if the capability exists in the store
	*/
	return useStore(store, (state) => hasCapability(state, capability));
};

//#endregion
//#region src/components/capability-guard.tsx
const CapabilityGuard = ({ capability, children, fallback = null }) => {
	/**
	* Check if the user has the specified capability
	*/
	const hasCapability$1 = useHasCapability(capability);
	return /* @__PURE__ */ jsx(Show, {
		when: hasCapability$1,
		fallback,
		children
	});
};

//#endregion
//#region src/utils/array-to-keyed-object.ts
/**
*
* Converts an array to an object of the desired key
*
* @param arr The array of items. Cannot be a tuple
* @param key The key of the array items that should become the key of the object
* @returns an object whose keys represent match the value of the key provided
*
* @example
*
* const example = [
*   {
*     bar: "hello",
*   },
*   {
*     bar: "world"
*   }
* ];
*
* const myObj = arrayToKeyedObject(example, "bar");
*
* {
*   hello: {
*      bar: "hello"
*   },
*   world: {
*     bar: "world"
*   }
* }
*
*
*/
const arrayToKeyedObject = (arr, key) => {
	const obj = {};
	for (const el of arr) {
		const value = el[key];
		obj[value] = el;
	}
	return obj;
};

//#endregion
//#region src/stores/capabilities.store.ts
const createCapabilitiesStore = (capabilities) => {
	const store = new Store({ capabilities: new Map(Object.entries(arrayToKeyedObject(capabilities, "id"))) });
	/**
	* Overwrites the existing capabilities with the ones provided
	*
	* @param capabilities capabilities to use to set the store value
	*/
	const setCapabilities = (capabilities$1) => {
		store.setState((curr) => ({
			...curr,
			capabilities: new Map(Object.entries(arrayToKeyedObject(capabilities$1, "id")))
		}));
		return store.state;
	};
	return {
		hasCapability: (capability) => hasCapability(store.state, capability),
		setCapabilities,
		store
	};
};

//#endregion
export { CapabilitiesContext, CapabilitiesProvider, CapabilityGuard, createCapabilitiesStore, hasCapability, useCapabilitiesContext, useHasCapability };
//# sourceMappingURL=index.js.map